import { Button, CloseButton } from '@ui/shared/Button';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import range from 'lodash/range';
import zip from 'lodash/zip';
import * as React from 'react';
import { take } from 'rxjs/operators';
import copDataKeys from '../../../../constants/cop_data_keys';
import forceDataKeys from '../../../../constants/force_data_keys';
import sensorsDataKeys from '../../../../constants/sensor_data_keys';
import { TEST_EXPORT_TYPES } from '../../../../constants/test_export_types';
import { copProvider } from '../../../../data/providers/copProvider';
import { rawProvider } from '../../../../data/providers/rawProvider';
import { repetitionsProvider } from '../../../../data/providers/repetitionsProvider';
import { sensorsProvider } from '../../../../data/providers/sensorsProvider';
import { exportDateFormatSettingProvider } from '../../../../data/providers/settings/exportDateFormatSettingProvider';
import { singleTest } from '../../../../data/providers/testsProvider';
import {
  EZScorePreference,
  IAggregated,
  IMetric,
  IRawTest,
  ISelectOptionNumber,
  ISensors,
  ITest,
  ITestRepetition,
  Level,
} from '../../../../data/types';
import { saveExportDateFormatSetting } from '../../../../data/writers/settings/exportDateFormatSetting';
import {
  ValidExportDateFormat,
  buildRepetitionCSV,
  concatTagNames,
  downloadCOPCSV,
  downloadDisplacementCSV,
  downloadForceCSV,
  downloadPowerCSV,
  downloadRsiCSV,
  downloadSensorsCSV,
  downloadSingleTestCSV,
  downloadTruStrengthRawCSV,
  downloadVelocityCSV,
  makeFilename,
  makeTestWithComparisonCSV,
} from '../../../../utils/export';
import { roundValue } from '../../../../utils/math';
import { Card } from '../../../shared/card/Card';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ExportDateFormat } from '../../../shared/form/ExportDateFormat';
import { FormContainer } from '../../../shared/form/FormContainer';
import { Select } from '../../../shared/form/Select';
import { SelectBox } from '../../../shared/form/SelectBox';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';

interface IProps {
  closeDialog: () => void;
  periodAggregates: IAggregated;
  selectedPeriod: number | string;
  metrics: IMetric[];
  test: ITest;
  startDate: ISelectOptionNumber;
  endDate: ISelectOptionNumber;
  useZPercentile?: EZScorePreference;
  level?: Level;
}

const defaultOptions: ISelectOptionNumber[] = [
  { label: 'Calculations', value: TEST_EXPORT_TYPES.CALCS },
  {
    label: 'Calculations w/ Comparison',
    value: TEST_EXPORT_TYPES.CALCS_WITH_COMPARISON,
  },
  { label: 'Force', value: TEST_EXPORT_TYPES.FORCE },
  { label: 'Sensors', value: TEST_EXPORT_TYPES.SENSORS },
  { label: 'COP', value: TEST_EXPORT_TYPES.COP },
];

const velocityBasedOptions: ISelectOptionNumber[] = [
  { label: 'Velocity', value: TEST_EXPORT_TYPES.VELOCITY },
  { label: 'Power', value: TEST_EXPORT_TYPES.POWER },
  { label: 'Displacement', value: TEST_EXPORT_TYPES.DISPLACEMENT },
];

const rsiOption: ISelectOptionNumber[] = [
  { label: 'RSI', value: TEST_EXPORT_TYPES.RSI },
];

const repetitionOption: ISelectOptionNumber[] = [
  { label: 'Repetitions', value: TEST_EXPORT_TYPES.REPETITIONS },
];
/**
 *
 *
 * @param {IProps} props
 * @returns {React.ReactElement}
 */
const ExportPopup = (props: IProps): React.ReactElement => {
  const [selectedOption, setSelectedOption] =
    React.useState<ISelectOptionNumber>(defaultOptions[0]);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [options, setOptions] = React.useState<ISelectOptionNumber[]>([]);

  const [includeInactiveMetrics, setIncludeInactiveMetrics] =
    React.useState<boolean>(false);
  const [deidentify, setDeidentify] = React.useState<boolean>(false);
  const [useDecimalComma, setUseDecimalComma] = React.useState<boolean>(false);
  const [dateFormat, setDateFormat] = React.useState<ValidExportDateFormat>();
  const [testToExport, setTestToExport] = React.useState<ITest>();

  /**
   * @description subscribe to export date format setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = exportDateFormatSettingProvider.subscribe(
      (value: ValidExportDateFormat): void => setDateFormat(value)
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  React.useEffect((): (() => void) => {
    const subscriber = singleTest(
      props.test.id,
      includeInactiveMetrics,
      deidentify
    )
      .pipe(take(1))
      .subscribe((test: ITest): void => {
        setTestToExport(test);
      });
    return (): void => subscriber.unsubscribe();
  }, [props.test, includeInactiveMetrics, deidentify]);

  const onExport = (): void => {
    switch (selectedOption.value) {
      case TEST_EXPORT_TYPES.CALCS:
        exportCalcs();
        break;

      case TEST_EXPORT_TYPES.CALCS_WITH_COMPARISON:
        exportCalcsWithComparison();
        break;

      case TEST_EXPORT_TYPES.FORCE:
        exportForce();
        break;

      case TEST_EXPORT_TYPES.POWER:
        exportPower();
        break;

      case TEST_EXPORT_TYPES.RSI:
        exportRSI();
        break;

      case TEST_EXPORT_TYPES.DISPLACEMENT:
        exportDisplacement();
        break;

      case TEST_EXPORT_TYPES.VELOCITY:
        exportVelocity();
        break;

      case TEST_EXPORT_TYPES.REPETITIONS:
        exportRepetitions();
        break;
      case TEST_EXPORT_TYPES.SENSORS:
        exportSensors();
        break;

      case TEST_EXPORT_TYPES.COP:
        exportCOP();
        break;
    }
  };

  const download = (): void => {
    setIsDisabled(true);
    onExport();
  };

  const onSelectChange = (value: ISelectOptionNumber): void => {
    setSelectedOption(value);
  };

  const exportCalcs = async (): Promise<void> => {
    const exportData = {
      ...testToExport,
    };
    const filename = makeFilename(
      exportData.athlete.name,
      exportData.testType.name
    );

    const time = dayjs(exportData.timestamp).format('MM_DD_YY_hh:mm:ss');
    downloadSingleTestCSV(
      exportData,
      filename,
      time,
      dateFormat,
      useDecimalComma
    );
    props.closeDialog();
  };

  const exportForce = (): void => {
    const { id, athlete, testType, tags } = testToExport;

    rawProvider(id).subscribe((data: IRawTest): void => {
      const forceArrays: number[][] = forceDataKeys.map(
        (key: string): number[] => data[key]
      );
      // use the combined Z Force as the length (index[2])
      const length: number = forceArrays[2].length;
      const raw = zip(
        range(length).map((index: number): number =>
          convertIndexToMillseconds(index, data.hertz ?? 1000)
        ),
        ...forceArrays
      );
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        deidentify ? athlete.id : athlete.name,
        `${testType.name}${tagNames}`
      );
      const time = dayjs(data.timestamp).format('MM_DD_YY_hh:mm:ss');
      downloadForceCSV(raw, filename, time, useDecimalComma);
      props.closeDialog();
    });
  };

  const exportCOP = async (): Promise<void> => {
    const { id, athlete, testType, tags } = testToExport;

    const data = await copProvider(id);

    const forceArrays: Array<Array<number | string>> = copDataKeys.map(
      (key: string): number[] => data[key]
    );

    const length: number = forceArrays[0].length;
    const raw = zip(
      range(length).map((index: number): number =>
        convertIndexToMillseconds(index, data.hertz ?? 1000)
      ),
      ...forceArrays
    );
    const tagNames = concatTagNames(tags);
    const filename = makeFilename(
      deidentify ? athlete.id : athlete.name,
      `${testType.name}${tagNames}`
    );
    downloadCOPCSV(raw, filename, useDecimalComma);
    props.closeDialog();
  };

  const exportSensors = (): void => {
    const { id, athlete, testType, tags } = testToExport;

    sensorsProvider(id).subscribe((data: ISensors): void => {
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        deidentify ? athlete.id : athlete.name,
        `${testType.name}${tagNames}`
      );

      // Check if this is TruStrength data
      if (data.trustrengthRaw && data.trustrengthRaw.length > 0) {
        // Export TruStrength raw ADC values
        const length: number = data.trustrengthRaw.length;
        const raw = zip(
          range(length).map((index: number): number =>
            convertIndexToMillseconds(index, data.hertz ?? 1000)
          ),
          data.trustrengthRaw,
          data.trustrengthRatedOutput
            ? Array(length).fill(data.trustrengthRatedOutput)
            : []
        );
        downloadTruStrengthRawCSV(
          raw,
          filename,
          useDecimalComma,
          data.trustrengthRatedOutput
        );
      } else {
        // Export force plate sensor data
        const forceArrays: number[][] = sensorsDataKeys
          .filter((key: string): boolean => key !== 'trustrengthRaw')
          .map((key: string): number[] => data[key]);
        const length: number = forceArrays[forceArrays.length - 1].length;
        const raw = zip(
          range(length).map((index: number): number =>
            convertIndexToMillseconds(index, data.hertz ?? 1000)
          ),
          ...forceArrays
        );
        downloadSensorsCSV(raw, filename, useDecimalComma);
      }

      props.closeDialog();
    });
  };

  const exportPower = (): void => {
    const { id, athlete, testType, tags } = testToExport;
    rawProvider(id).subscribe((data: IRawTest): void => {
      const power = data.power;
      const raw = zip(
        range(power.length).map((index: number): number =>
          convertIndexToMillseconds(index, data.hertz ?? 1000)
        ),
        power
      );
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        athlete.name,
        `${testType.name}${tagNames}`
      );
      const time = dayjs(data.timestamp).format('MM_DD_YY_hh:mm:ss');
      downloadPowerCSV(raw, filename, time, useDecimalComma);
      props.closeDialog();
    });
  };

  const exportRepetitions = (): void => {
    repetitionsProvider(
      testToExport.id,
      deidentify,
      includeInactiveMetrics
    ).subscribe((repetitions: ITestRepetition[]): void => {
      const filename = makeFilename(
        testToExport.athlete.name,
        testToExport.testType.name
      );
      const time = dayjs(testToExport.timestamp).format('MM_DD_YY_hh:mm:ss');
      const csv = buildRepetitionCSV(repetitions, dateFormat);

      const fileDescription = `${filename}-${time}.csv`;
      saveAs(
        encodeURI(['text/csv;charset=utf-8', csv].join(',')),
        fileDescription
      );
    });
  };

  const exportVelocity = (): void => {
    const { id, athlete, testType, tags } = testToExport;
    rawProvider(id).subscribe((data: IRawTest): void => {
      const velocities = data.velocities;
      const raw = zip(
        range(velocities.length).map((index: number): number =>
          convertIndexToMillseconds(index, data.hertz ?? 1000)
        ),
        velocities
      );
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        deidentify ? athlete.id : athlete.name,
        `${testType.name}${tagNames}`
      );
      const time = dayjs(data.timestamp).format('MM_DD_YY_hh:mm:ss');
      downloadVelocityCSV(raw, filename, time, useDecimalComma);
      props.closeDialog();
    });
  };

  const exportRSI = (): void => {
    const { id, athlete, testType, tags } = testToExport;
    rawProvider(id).subscribe((data: IRawTest): void => {
      const rsi = data.rsi.map((i: number): string => i.toFixed(2));
      const raw = zip(
        range(rsi.length).map((i: number): string => `Jump ${i + 1}`),
        rsi
      );
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        deidentify ? athlete.id : athlete.name,
        `${testType.name}${tagNames}`
      );
      const time = dayjs(data.timestamp).format('MM_DD_YY_hh:mm:ss');
      downloadRsiCSV(raw, filename, time);
      props.closeDialog();
    });
  };

  const exportDisplacement = (): void => {
    const { id, athlete, testType, tags } = testToExport;
    rawProvider(id).subscribe((data: IRawTest): void => {
      const displacements = data.displacements;
      const raw = zip(
        range(displacements.length).map((index: number): number =>
          convertIndexToMillseconds(index, data.hertz ?? 1000)
        ),
        displacements
      );
      const tagNames = concatTagNames(tags);
      const filename = makeFilename(
        deidentify ? athlete.id : athlete.name,
        `${testType.name}${tagNames}`
      );
      const time = dayjs(data.timestamp).format('MM_DD_YY_hh:mm:ss');
      downloadDisplacementCSV(raw, filename, time, useDecimalComma);
      props.closeDialog();
    });
  };

  const exportCalcsWithComparison = (): void => {
    const filename = makeFilename(
      testToExport.athlete.name,
      testToExport.testType.name
    );
    const time = dayjs(testToExport.timestamp).format('MM_DD_YY_hh:mm:ss');
    makeTestWithComparisonCSV(
      props?.periodAggregates,
      testToExport,
      filename,
      time,
      useDecimalComma,
      props.selectedPeriod,
      props.startDate,
      props.endDate,
      props.useZPercentile
    );
    props.closeDialog();
  };

  /**
   * @description converts index to milliseconds
   * @param index
   * @param hertz
   * @returns
   */
  const convertIndexToMillseconds = (index: number, hertz: number): number =>
    roundValue((index + 1) / hertz, 4);

  /**
   * @description create the export options
   */
  React.useEffect((): void => {
    if (!testToExport) return setOptions(defaultOptions);
    const { testType } = testToExport;
    const mOptions: ISelectOptionNumber[] = [].concat(defaultOptions);
    if (testType.velocity) {
      mOptions.push(...velocityBasedOptions);
    }
    if (testType.rsi) {
      mOptions.push(...rsiOption);
    }

    if (testType?.repetitions) {
      mOptions.push(...repetitionOption);
    }

    setOptions(mOptions);
  }, [testToExport]);

  /**
   * @description handles user selecting inactive metrics in the export
   */
  const handleInactiveMetricsSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setIncludeInactiveMetrics(e.currentTarget.checked);
  };

  /**
   * @description handles changes to export date format
   * @param value
   */
  const handleExportDateFormatSelection = (
    value: ValidExportDateFormat
  ): void => {
    saveExportDateFormatSetting(value);
  };

  /**
   * @description handles user deidentification selection
   * @param e
   * @returns
   */
  const handleDeidentificationOption = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => setDeidentify(e.currentTarget.checked);

  /**
   * @description handles user selecting use deicmal comma in export
   */
  const handleUseDecimalComma = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setUseDecimalComma(e.currentTarget.checked);
  };

  const Container = props.level === 2 ? Card : InnerPopupCard;

  return (
    <Container onClose={props.closeDialog} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Export Test</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.closeDialog} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <FormContainer style={{ alignItems: 'flex-start' }}>
            <ControlSpacer style={{ width: '100%' }}>
              <ControlLabel>Export Type</ControlLabel>
              <Select
                options={options}
                value={selectedOption}
                onChange={onSelectChange}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Include Inactive Metrics'
                selected={includeInactiveMetrics}
                onSelect={handleInactiveMetricsSelection}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Deidentify Data'
                selected={deidentify}
                onSelect={handleDeidentificationOption}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Use Decimal Comma (EUR)'
                selected={useDecimalComma}
                onSelect={handleUseDecimalComma}
              />
            </ControlSpacer>
            <ControlSpacer>
              <ExportDateFormat
                onChange={handleExportDateFormatSelection}
                value={dateFormat}
              />
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <Button
              onClick={download}
              disabled={isDisabled}
              icon='cloud_download'
              title='Export Test'
              style={{ whiteSpace: 'nowrap' }}
             />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </Container>
  );
};

export { ExportPopup };
